import { FC, memo } from "react";
import { Button } from "@mui/material";
import { observer } from "mobx-react";

import { IFileAttachment } from "../../../../types/chat";
import Row from "../../../ui/core/Row";
import AttachedFileIcon from "../../../icons/AttachedFile";
import { DownloadIcon } from "../../../icons";
import { ChatHelper } from "../../../helpers/chatHelper";
import { useChatStore } from "../../../../hooks/useChatStore";
import { useCapabilities } from "../../../../contexts/permissions/useCapabilities";

const ACTION_BUTTONS_STYLES = {
  display: "inline-flex",
  alignItems: "center",
  color: "text.primary",
  borderRadius: 2,
  borderColor: "custom.safeGreen",
  backgroundColor: "custom.safeLightGreen",
};

interface ChatMessageFileBoxProps {
  file: IFileAttachment;
  index: number;
}

const ChatMessageFileBox: FC<ChatMessageFileBoxProps> = observer(({ file, index }) => {
  const chatStore = useChatStore();
  const { canDownloadAttachments } = useCapabilities();

  const fileName =
    file.original_name || file.file_name || file.name || `file-${index + 1}`;

  const onFileView = () => {
    chatStore.selectFileToPreview(file, fileName);
  };

  const onDownloadClick = () => {
    ChatHelper.downloadFile(file);
  };

  return (
    <Row
      sx={{ justifyContent: "flex-end", gridGap: "5px", alignItems: "center" }}
    >
      <Button
        variant="outlined"
        onClick={onFileView}
        title="View file"
        startIcon={<AttachedFileIcon width={15} height={15} />}
        sx={{
          ...ACTION_BUTTONS_STYLES,
          gap: 1,
          fontSize: "var(--font-size-md, 1rem)",
          padding: "3px 15px !important",
          textTransform: "none",
          fontWeight: "600",
          ".MuiButton-startIcon": {
            marginRight: "0px",
          },
          "&:hover": {
            textDecoration: "underline",
          },
        }}
      >
        {file.display_name || fileName}
      </Button>

      {canDownloadAttachments ? (
        <Button
          variant="outlined"
          size="small"
          onClick={onDownloadClick}
          title="Download file"
          sx={{
            ...ACTION_BUTTONS_STYLES,
            height: "25px",
            color: "text.primary",
            borderRadius: 2,
            borderColor: "custom.safeGreen",
            backgroundColor: "background.paper",
            flexShrink: 0,
            minWidth: "auto",
            "&:hover": {
              backgroundColor: "custom.safeGreen",
              color: "primary.contrastText",
              borderColor: "custom.safeGreen",
            },
          }}
        >
          <DownloadIcon size={13} />
        </Button>
      ) : null}
    </Row>
  );
});

export default memo(ChatMessageFileBox);
